import { API_BASE, buildAuthHeaders } from "./base";

const LOGIN_TIMEOUT_MS = 30000;

async function handleResponse(res) {
  const contentType = res.headers.get("content-type") || "";
  let body = null;
  let rawText = "";

  if (contentType.includes("application/json")) {
    body = await res.json().catch(() => null);
  } else {
    rawText = await res.text().catch(() => "");
    if (rawText) {
      try {
        body = JSON.parse(rawText);
      } catch {
        body = null;
      }
    }
  }

  if (!res.ok) {
    const detail = body?.detail;
    let message = null;
    if (typeof detail === "string") {
      message = detail;
    } else if (detail?.detail) {
      message = detail.detail;
    } else if (body?.message) {
      message = body.message;
    } else if (detail?.message) {
      message = detail.message;
    } else if (rawText) {
      message = rawText.trim();
    } else if (res.status >= 500) {
      message = "Sign-in is temporarily unavailable. Please try again in a few minutes.";
    }
    const error = new Error(message || "Authentication failed");
    error.status = res.status;
    error.payload = body;
    throw error;
  }
  if (!body || typeof body !== "object") {
    throw new Error("Unexpected login response. Refresh the page and try again.");
  }
  return body;
}

export async function login(accountId, identifier, password, options = {}) {
  const payload = new FormData();
  const normalizedAccountId = String(accountId ?? "").trim();
  const normalizedIdentifier = String(identifier ?? "").trim();
  const accountMode = String(options.accountMode || "").trim().toLowerCase();
  const usernamePrefix =
    accountMode === "accountant"
      ? "accountant"
      : accountMode === "independent_professional"
      ? `independent_professional:${normalizedAccountId}`
      : normalizedAccountId;
  payload.append("username", `${usernamePrefix}:${normalizedIdentifier}`);
  payload.append("password", password);
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${API_BASE}/token`, {
      method: "POST",
      body: payload,
      credentials: "include",
      signal: controller.signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("Login timed out. Please try again.");
    }
    throw err;
  } finally {
    window.clearTimeout(timeoutId);
  }
  return handleResponse(res);
}

export async function completeOperatorMfa(
  challengeToken,
  code,
  rememberDevice = false
) {
  const res = await fetch(`${API_BASE}/auth/operator-mfa/complete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      challenge_token: challengeToken,
      code,
      remember_device: Boolean(rememberDevice),
    }),
  });
  return handleResponse(res);
}

export async function requestStaffEmailVerification(email) {
  const res = await fetch(`${API_BASE}/auth/email-verification/request`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      email,
    }),
  });
  return handleResponse(res);
}

export async function completeStaffEmailVerification(token) {
  const res = await fetch(`${API_BASE}/auth/email-verification/complete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      token,
    }),
  });
  return handleResponse(res);
}

export async function selectSalon(salonId, token) {
  const res = await fetch(`${API_BASE}/auth/select-salon`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(token),
    },
    body: JSON.stringify({ salon_id: salonId }),
    credentials: "include",
  });
  return handleResponse(res);
}

export async function getMySalons(token) {
  const res = await fetch(`${API_BASE}/me/salons`, {
    headers: buildAuthHeaders(token),
    credentials: "include",
  });
  return handleResponse(res);
}

export async function getStaffSession() {
  const res = await fetch(`${API_BASE}/auth/session`, {
    credentials: "include",
  });
  if (res.status === 401) {
    return null;
  }
  return handleResponse(res);
}

export async function createMarketplaceCrossLaunch() {
  const res = await fetch(`${API_BASE}/auth/cross-launch/marketplace`, {
    method: "POST",
    credentials: "include",
  });
  return handleResponse(res);
}

export async function createOpsCrossLaunch(nextPath = "") {
  const payload = {};
  const normalizedNextPath = String(nextPath || "").trim();
  if (normalizedNextPath) {
    payload.next_path = normalizedNextPath;
  }
  const res = await fetch(`${API_BASE}/auth/cross-launch/ops`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function completeMarketplaceCrossLaunch(launchToken) {
  const res = await fetch(`${API_BASE}/auth/cross-launch/marketplace/complete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      launch_token: launchToken,
    }),
  });
  return handleResponse(res);
}

export async function completeOpsCrossLaunch(launchToken) {
  const res = await fetch(`${API_BASE}/auth/cross-launch/ops/complete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      launch_token: launchToken,
    }),
  });
  return handleResponse(res);
}

export async function logoutStaffSession() {
  const res = await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
  if (res.status === 401) {
    return { ok: true };
  }
  return handleResponse(res);
}

export async function requestStaffPasswordReset(salonId, identifier) {
  const res = await fetch(`${API_BASE}/auth/password-reset/request`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      salon_id: Number(salonId),
      identifier,
    }),
  });
  return handleResponse(res);
}

export async function requestStaffSalonIds(email) {
  const res = await fetch(`${API_BASE}/auth/password-reset/salon-ids/request`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      email,
    }),
  });
  return handleResponse(res);
}

export async function validateStaffPasswordReset(token, salonId) {
  const params = new URLSearchParams({
    token,
    salon_id: String(salonId),
  });
  const res = await fetch(`${API_BASE}/auth/password-reset/validate?${params}`, {
    credentials: "include",
  });
  if (res.status === 400) {
    return { valid: false };
  }
  return handleResponse(res);
}

export async function completeStaffPasswordReset(payload) {
  const res = await fetch(`${API_BASE}/auth/password-reset/complete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}
